// Generated by tutorials/record.cjs from tutorials/articles/h01-map-ehr-text.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h01 --text-only` (wording) or
// `node tutorials/record.cjs h01` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'map-ehr-text',
  catalogId: 'h01',
  title: 'Map EHR text to Dietech',
  summary: 'Your EHR sends diet orders as text. Map each phrase once, and Dietech turns it into the right diet, consistency, allergy or special need for every resident from then on.',
  audience: 'Dietary managers and dietitians who look after the diet orders your EHR sends',
  area: 'residents',
  learn: [
    'Map waiting text one phrase at a time in the Map this pop-up',
    'Check what a mapping does before you save it',
    'Apply a new mapping to residents who already have the text',
    'Find and change mappings on the Mappings tab',
    'Read the badges and review allergy changes from your EHR',
  ],
  keywords: [
    'ehr',
    'emr',
    'mapping',
    'mappings',
    'map this',
    'diet order',
    'interface',
    'hl7',
    'waiting text',
    'allergy changes',
    'apply to current residents',
  ],
  steps: [
    {
      id: 'open-ehr',
      part: 'Map a phrase from your EHR',
      heading: 'Open the EHR page',
      text: 'In the menu, select Residents, then EHR. When text from your EHR is waiting to be mapped, the Map this pop-up opens by itself.',
      media: {
        image: {
          src: '/help/map-ehr-text/open-ehr.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/open-ehr.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Residents menu with EHR selected, and the Map this pop-up opening over the EHR page.',
      },
    },
    {
      id: 'one-phrase',
      part: 'Map a phrase from your EHR',
      heading: 'One phrase at a time',
      text: 'The pop-up shows one phrase your EHR sent, which part of the order it came in, and how many messages and residents are waiting on it.\n\nText that is holding messages comes first, and allergies come first of all.',
      media: {
        image: {
          src: '/help/map-ehr-text/one-phrase.webp',
          width: 1280,
          height: 768,
        },
        alt: 'The Map this pop-up showing the phrase "Fish allergy", marked Allergy, with 2 messages and 2 residents waiting.',
      },
    },
    {
      id: 'map-as',
      part: 'Map a phrase from your EHR',
      heading: 'Pick what it means',
      text: 'Under Map as, pick what the phrase is. For an allergy, select Food allergy, then the allergy from your list.\n\nAllergies never start picked for you, so someone always chooses them on purpose.',
      media: {
        image: {
          src: '/help/map-ehr-text/map-as.webp',
          width: 1280,
          height: 890,
        },
        clip: {
          src: '/help/map-ehr-text/map-as.mp4',
          width: 1280,
          height: 890,
          durationSec: 3.4,
        },
        alt: 'Food allergy is selected under Map as, and Fish is selected from the list of food allergies.',
      },
    },
    {
      id: 'preview',
      part: 'Map a phrase from your EHR',
      heading: 'Check what saving does',
      text: 'When you save tells you what the mapping will add and how many held messages it lets through. Nothing changes until you save.',
      media: {
        image: {
          src: '/help/map-ehr-text/preview.webp',
          width: 1280,
          height: 890,
        },
        alt: 'The When you save box, saying the food allergy Fish will be added and the held messages released.',
      },
    },
    {
      id: 'view-residents',
      part: 'Map a phrase from your EHR',
      heading: 'See who it is for',
      text: 'Not sure what your EHR meant? Select View residents for context. You see each resident, their room and the full line from their order, with the phrase highlighted.',
      media: {
        image: {
          src: '/help/map-ehr-text/view-residents.webp',
          width: 1280,
          height: 1050,
        },
        clip: {
          src: '/help/map-ehr-text/view-residents.mp4',
          width: 1280,
          height: 1050,
          durationSec: 2.6,
        },
        alt: 'A table of two residents with their rooms and the line from their order, with "Fish allergy" highlighted.',
      },
    },
    {
      id: 'save-next',
      part: 'Map a phrase from your EHR',
      heading: 'Save and go to the next phrase',
      text: 'Select Save and next. The held messages go through on the next pass, and the pop-up moves on to the next phrase.',
      media: {
        image: {
          src: '/help/map-ehr-text/save-next.webp',
          width: 1280,
          height: 1026,
        },
        clip: {
          src: '/help/map-ehr-text/save-next.mp4',
          width: 1280,
          height: 1026,
          durationSec: 2.9,
        },
        alt: 'After saving, the pop-up shows the next phrase, "Mech soft".',
      },
    },
    {
      id: 'suggestion',
      part: 'Map a phrase from your EHR',
      heading: 'Check the suggestion',
      text: 'When Dietech has seen a phrase before, at your facility or at other facilities with the same EHR, it suggests a mapping and picks it for you. Check that it is right before you save.',
      media: {
        image: {
          src: '/help/map-ehr-text/suggestion.webp',
          width: 1280,
          height: 1026,
        },
        alt: 'The phrase "Mech soft" with Consistency and Mechanical Soft already picked, and the suggestion below.',
      },
    },
    {
      id: 'extra-lines',
      part: 'Map a phrase from your EHR',
      heading: 'Lines that hold nothing',
      text: 'Some lines, like "No straws", do not hold messages. Until they are mapped, they are copied into a meal pattern note.\n\nMap them as what they mean, such as a special need or a device, or choose Keep as note to keep the note and take the line off the list.',
      media: {
        image: {
          src: '/help/map-ehr-text/extra-lines.webp',
          width: 1280,
          height: 998,
        },
        clip: {
          src: '/help/map-ehr-text/extra-lines.mp4',
          width: 1280,
          height: 998,
          durationSec: 2.8,
        },
        alt: 'The phrase "No straws" in the group Not holding, but nothing is applied, with Special need picked.',
      },
    },
    {
      id: 'apply-offer',
      part: 'Apply to current residents',
      heading: 'Apply it to residents who already have it',
      text: 'After you save, Dietech looks at the residents in house whose latest order already has the text. If any of them would change, it offers to apply the mapping to them now.',
      media: {
        image: {
          src: '/help/map-ehr-text/apply-offer.webp',
          width: 1280,
          height: 848,
        },
        clip: {
          src: '/help/map-ehr-text/apply-offer.mp4',
          width: 1280,
          height: 848,
          durationSec: 2.8,
        },
        alt: 'A note above the next phrase: 2 current residents already have "NO STRAWS" in their latest diet order, with Apply to current residents.',
      },
    },
    {
      id: 'apply-preview',
      part: 'Apply to current residents',
      heading: 'Look before you apply',
      text: 'Select Apply to current residents. The list shows each resident, the line from their latest order, and exactly what would be added.\n\nOnly what is missing is added. Nothing anyone entered is changed or removed.',
      media: {
        image: {
          src: '/help/map-ehr-text/apply-preview.webp',
          width: 1280,
          height: 646,
        },
        clip: {
          src: '/help/map-ehr-text/apply-preview.mp4',
          width: 1280,
          height: 646,
          durationSec: 2,
        },
        alt: 'The Apply "NO STRAWS" to current residents window, listing three residents and what changes for each.',
      },
    },
    {
      id: 'apply-confirm',
      part: 'Apply to current residents',
      heading: 'Apply',
      text: 'Select Apply. Dietech makes the changes for the residents in the list and tells you how many it made.',
      media: {
        image: {
          src: '/help/map-ehr-text/apply-confirm.webp',
          width: 1280,
          height: 328,
        },
        clip: {
          src: '/help/map-ehr-text/apply-confirm.mp4',
          width: 1280,
          height: 328,
          durationSec: 2.3,
        },
        alt: 'The window now says: Made 2 changes for 2 residents.',
      },
    },
    {
      id: 'skip',
      part: 'Apply to current residents',
      heading: 'Skip a phrase for now',
      text: 'Not ready to map a phrase? Select Skip for now. It stays on the list for next time.\n\nSupplement orders such as "Juven BID" have their own article: Automatic supplements and tube feeds from your EHR.',
      media: {
        image: {
          src: '/help/map-ehr-text/skip.webp',
          width: 1280,
          height: 744,
        },
        alt: 'The phrase "Juven BID" with the Skip for now button marked.',
      },
    },
    {
      id: 'mappings-tab',
      part: 'Find and change a mapping',
      heading: 'Open the Mappings tab',
      text: 'Close the pop-up and select Mappings. It lists every mapping your facility has. The banner at the top says how much text is still waiting, and Map waiting text opens the pop-up again.',
      media: {
        image: {
          src: '/help/map-ehr-text/mappings-tab.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/mappings-tab.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'The Mappings tab: a banner about waiting text, then the list of Diet Type mappings.',
      },
    },
    {
      id: 'kinds',
      part: 'Find and change a mapping',
      heading: 'Find a mapping',
      text: 'Pick a kind, such as Diet Type, Consistency or Allergies, to see its mappings. Type in Search to narrow the list.',
      media: {
        image: {
          src: '/help/map-ehr-text/kinds.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/kinds.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.4,
        },
        alt: 'The Consistency mappings, narrowed to PUREE by typing in Search.',
      },
    },
    {
      id: 'edit',
      part: 'Find and change a mapping',
      heading: 'Change a mapping',
      text: 'Select a mapping, then Edit to change what it maps to. History shows who changed it and when.',
      media: {
        image: {
          src: '/help/map-ehr-text/edit.webp',
          width: 944,
          height: 920,
        },
        clip: {
          src: '/help/map-ehr-text/edit.mp4',
          width: 944,
          height: 920,
          durationSec: 2,
        },
        alt: 'The window for editing the PUREE mapping, which maps to the consistency Pureed.',
      },
    },
    {
      id: 'apply-existing',
      part: 'Find and change a mapping',
      heading: 'Apply a mapping you already have',
      text: 'For special needs, devices and feeding status, Apply to Current Residents does the same as the offer after a save: it shows who would change first, then applies.',
      media: {
        image: {
          src: '/help/map-ehr-text/apply-existing.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Special Needs mappings with CUT UP MEAT selected and the Apply to Current Residents button marked.',
      },
    },
    {
      id: 'health',
      part: 'Find and change a mapping',
      heading: 'Check mapping health',
      text: 'Mapping health points out mappings that repeat or clash, mappings nothing has used lately, and text that keeps arriving without a mapping.',
      media: {
        image: {
          src: '/help/map-ehr-text/health.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/health.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Mapping health section, listing one mapping that has not been used in 60 days.',
      },
    },
    {
      id: 'badges',
      part: 'Review allergy changes from your EHR',
      heading: 'Watch the badges',
      text: 'The number on Mappings is the text waiting to be mapped. It is red when that text is holding messages. The number on Allergy Changes is the changes waiting for your review.',
      media: {
        image: {
          src: '/help/map-ehr-text/badges.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The EHR tabs: Mappings with a red 2 and Allergy Changes with a 3.',
      },
    },
    {
      id: 'allergy-changes',
      part: 'Review allergy changes from your EHR',
      heading: 'Review allergy changes',
      text: 'Select Allergy Changes. It lists every allergy your EHR added to or removed from a resident, so a dietitian can check each one.',
      media: {
        image: {
          src: '/help/map-ehr-text/allergy-changes.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/allergy-changes.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.6,
        },
        alt: 'The Allergy Changes tab listing three changes: two allergies added and one removed.',
      },
    },
    {
      id: 'mark-reviewed',
      part: 'Review allergy changes from your EHR',
      heading: 'Mark them reviewed',
      text: 'Check the changes you have reviewed, then select Mark Reviewed. Your name and the time are recorded, and the changes move to Reviewed.',
      media: {
        image: {
          src: '/help/map-ehr-text/mark-reviewed.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/map-ehr-text/mark-reviewed.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.7,
        },
        alt: 'After Mark Reviewed: a note says 2 changes were marked reviewed, and one change is still waiting.',
      },
    },
  ],
  tips: [
    'Not sure what a phrase means? Ask whoever writes the orders in your EHR before you map it. A mapping is used for every order that has that text from then on.',
    'Use Do Not Process for text that is not about diet, such as "See physician order". Dietech then ignores it for good.',
    'If the Map this pop-up never opens for you, your role may not include EHR mapping. Ask your administrator.',
  ],
  notes: [
    'Allergies always hold their messages until they are mapped, so map them first. They are never picked for you.',
    'Apply to current residents only fills in what is missing: special needs, devices and feeding status. Diet types, consistencies and allergies follow each resident\'s next order from your EHR.',
    'Nothing changes for a resident until you save. Skip for now leaves a phrase on the list for next time.',
  ],
  related: ['ehr-supplements-tube-feeds'],
  recorded: '2026-10-25',
};

export default article;
